import { useEffect, useRef } from "react";

export interface IdleLock {
  check: () => void;
  start: () => void;
  stop: () => void;
  touch: () => void;
}

export function createIdleLock(options: {
  delayMs: number;
  now?: () => number;
  onIdle: () => void;
}): IdleLock {
  const now = options.now ?? Date.now;
  let handle: ReturnType<typeof setTimeout> | null = null;
  let lastActivity = now();
  let fired = false;

  function clear() {
    if (handle !== null) clearTimeout(handle);
    handle = null;
  }
  function fire() {
    clear();
    if (fired) return;
    fired = true;
    options.onIdle();
  }
  function arm() {
    clear();
    handle = setTimeout(fire, options.delayMs);
  }

  return {
    // A background tab can hold its timer for longer than the delay; this
    // catches that case when the page is shown again.
    check() {
      if (
        !fired &&
        handle !== null &&
        now() - lastActivity >= options.delayMs
      ) {
        fire();
      }
    },
    start() {
      fired = false;
      lastActivity = now();
      arm();
    },
    stop: clear,
    touch() {
      if (fired) return;
      lastActivity = now();
      arm();
    },
  };
}

const ACTIVITY_EVENTS = [
  "keydown",
  "pointerdown",
  "scroll",
  "touchstart",
  "wheel",
] as const;

export function useIdleLock(delayMs: number | null, onIdle: () => void): void {
  const callback = useRef(onIdle);
  callback.current = onIdle;

  useEffect(() => {
    if (delayMs === null) return undefined;
    const lock = createIdleLock({
      delayMs,
      onIdle: () => callback.current(),
    });
    const touch = () => lock.touch();
    const visible = () => {
      if (document.visibilityState === "visible") lock.check();
    };
    lock.start();
    for (const name of ACTIVITY_EVENTS) {
      window.addEventListener(name, touch, { capture: true, passive: true });
    }
    document.addEventListener("visibilitychange", visible);
    return () => {
      lock.stop();
      for (const name of ACTIVITY_EVENTS) {
        window.removeEventListener(name, touch, { capture: true });
      }
      document.removeEventListener("visibilitychange", visible);
    };
  }, [delayMs]);
}
